import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import './Salon.css';

const AddMemberModal = ({ isOpen, onClose, salonId, onMemberAdded }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUserLimitError, setIsUserLimitError] = useState(false);
  const searchTimeoutRef = useRef(null);
  const modalRef = useRef(null);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setSearchResults([]);
      setSelectedUser(null);
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setSelectedUser(null);
    setError('');
    setSuccess('');

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Search after user stops typing for 300ms
    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(value);
      }, 300);
    } else {
      setSearchResults([]);
    }
  };

  const searchUsers = async (searchTerm) => {
    if (searchTerm.trim().length < 2) return;

    console.log('Searching for users with term:', searchTerm);
    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Making request to:', `/api/users/search?email=${encodeURIComponent(searchTerm)}`);
      console.log('Token exists:', !!token);
      
      const response = await axios.get(`/api/users/search?email=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('Search response:', response.data);
      setSearchResults(response.data.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
      console.error('Error details:', error.response?.data);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setEmail(user.email);
    setSearchResults([]);
  };

  const handleAddMember = async () => {
    if (!selectedUser) {
      setError(t('settings.selectUserFromResults'));
      return;
    }

    setIsAdding(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/add-member`, {
        userId: selectedUser.id,
        role: 'member'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccess(`${t('settings.successfullyAdded')} ${selectedUser.email} ${t('settings.toSalon')}`);
      setTimeout(() => {
        onMemberAdded();
        onClose();
      }, 1500);
    } catch (error) {
      if (error.response?.data?.code === 'USER_LIMIT_REACHED') {
        const currentUsers = error.response.data.currentUsers;
        const maxUsers = error.response.data.maxUsers;
        setError(
          `${t('subscription.userLimitReached')} (${currentUsers}/${maxUsers}). ${t('subscription.upgradeToAddMore')}`
        );
        setIsUserLimitError(true);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
        setIsUserLimitError(false);
      } else {
        setError(t('settings.failedToAddMember'));
        setIsUserLimitError(false);
      }
    } finally {
      setIsAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h3>{t('settings.addMemberToSalon')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="email">{t('settings.searchByEmail')}</label>
            <div className="email-search-container">
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={t('settings.enterEmailAddress')}
                className="email-input"
                autoComplete="off"
              />
              {isSearching && (
                <div className="search-spinner">
                  <div className="spinner"></div>
                </div>
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && !selectedUser && (
              <div className="search-results">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    className="search-result-item"
                    onClick={() => handleUserSelect(user)}
                  >
                    <div className="user-avatar">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <div className="user-info">
                      <div className="user-name">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="user-email">{user.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Selected User Display */}
            {selectedUser && (
              <div className="selected-user">
                <div className="user-avatar selected">
                  {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                </div>
                <div className="user-info">
                  <div className="user-name">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </div>
                  <div className="user-email">{selectedUser.email}</div>
                </div>
                <button
                  className="change-user-btn"
                  onClick={() => {
                    setSelectedUser(null);
                    setEmail('');
                  }}
                >
                  {t('settings.change')}
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              {error}
              {isUserLimitError && (
                <button
                  className="upgrade-link-button"
                  onClick={() => {
                    onClose();
                    navigate('/pricing');
                  }}
                  style={{ 
                    marginTop: '10px', 
                    display: 'block',
                    width: '100%',
                    padding: '10px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {t('subscription.viewPlans')}
                </button>
              )}
            </div>
          )}
          {success && <div className="success-message">{success}</div>}
        </div>

        <div className="modal-footer">
          <button className="modal-button secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="modal-button primary"
            onClick={handleAddMember}
            disabled={!selectedUser || isAdding}
          >
            {isAdding ? t('settings.adding') : t('settings.addMember')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
